import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { QuestionTemplate, QuestionItem } from '../../../types';
import { createQuestionTemplate, updateQuestionTemplate } from '../../../api/questionTemplateApi';
import { containsEmoji } from '../../../utils/Utils';

interface QuestionTemplateFormProps {
  show: boolean;
  onHide: () => void;
  template?: QuestionTemplate | null;
  onSuccess: () => void;
}

const QuestionTemplateForm: React.FC<QuestionTemplateFormProps> = ({
  show,
  onHide,
  template,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    questions: [] as QuestionItem[]
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        description: template.description || '',
        questions: template.questions || []
      });
    } else {
      setFormData({
        name: '',
        description: '',
        questions: [{ order: 1, text: '' }]
      });
    }
    setErrors({});
  }, [template, show]);

  const handleInputChange = (field: string, value: string): void => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleQuestionChange = (index: number, value: string): void => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, text: value } : q
      )
    }));
    const questionErrorKey = `question_${index}`;
    if (errors[questionErrorKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionErrorKey];
        return newErrors;
      });
    }
  };

  const addQuestion = (): void => {
    if (formData.questions.length < 10) {
      setFormData(prev => ({
        ...prev,
        questions: [
          ...prev.questions,
          { order: prev.questions.length + 1, text: '' }
        ]
      }));
    }
  };

  const removeQuestion = (index: number): void => {
    if (formData.questions.length > 1) {
      setFormData(prev => ({
        ...prev,
        questions: prev.questions
          .filter((_, i) => i !== index)
          .map((q, i) => ({ ...q, order: i + 1 }))
      }));
    }
  };

  const validateForm = (): {[key: string]: string} => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      newErrors.name = 'テンプレート名を入力してください。';
    } else if (containsEmoji(formData.name)) {
      newErrors.name = '絵文字は使用できません。';
    }

    if (formData.questions.length === 0) {
      newErrors.questions = '質問は最低1件必要です。';
    } else if (formData.questions.length > 10) {
      newErrors.questions = '質問は最大10件まで登録できます。';
    } else {
      for (let i = 0; i < formData.questions.length; i++) {
        if (!formData.questions[i].text.trim()) {
          newErrors[`question_${i}`] = `質問${i + 1}を入力してください。`;
        } else if (containsEmoji(formData.questions[i].text)) {
          newErrors[`question_${i}`] = '絵文字は使用できません。';
        }
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      const templateData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        questions: formData.questions.map((q, index) => ({
          order: index + 1,
          text: q.text.trim()
        }))
      };

      if (template) {
        await updateQuestionTemplate(template.id, templateData);
        alert('質問テンプレートを変更しました。');
      } else {
        await createQuestionTemplate(templateData);
        alert('質問テンプレートを作成しました。');
      }

      onSuccess();
    } catch (error: any) {
      if (error.response?.data?.errors?.name) {
        setErrors({ name: '既に登録済みのテンプレート名です。' });
      } else {
        alert(template ? '質問テンプレートの更新中にエラーが発生しました。' : '質問テンプレートの作成中にエラーが発生しました。');
      }
      console.error('Error saving template:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      className="question-template-input-modal"
      backdrop="static"  // Prevent close when clicking outside
      keyboard={false}   // Optional: Prevent closing with ESC key
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {template ? '質問テンプレート編集' : '質問テンプレート新規作成'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form>
          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>テンプレート名 <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="テンプレート名を入力してください"
                  isInvalid={!!errors.name}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
{/*
          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>説明</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="テンプレートの説明を入力してください"
                />
              </Form.Group>
            </Col>
          </Row>
*/}
          <div className="mb-3 question-input">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0">質問一覧 <span className="text-danger">*</span>　<small className="text-danger fw-normal">※ 最大10件まで登録できます</small></Form.Label>
              <Button 
                variant="outline-success" 
                size="sm" 
                onClick={addQuestion}
                disabled={formData.questions.length >= 10}
                className="detail-button"
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" />
                質問追加
              </Button>
            </div>
            {errors.questions && (
              <div className="invalid-feedback">
                {errors.questions}
              </div>
            )}

            {formData.questions.map((question, index) => (
              <div key={index} className="mb-2">
                <div className="d-flex align-items-center">
                  <span className='question-index'>{`Q${index + 1}.　`}</span>
                  <div className="flex-grow-1 me-2">
                    <Form.Control
                      type="text"
                      value={question.text}
                      onChange={(e) => handleQuestionChange(index, e.target.value)}
                      placeholder={`質問${index + 1}を入力してください`}
                      isInvalid={!!errors[`question_${index}`]}
                      required
                    />
                    {errors[`question_${index}`] && (
                      <Form.Control.Feedback type="invalid">
                        {errors[`question_${index}`]}
                      </Form.Control.Feedback>
                    )}
                  </div>
                  {formData.questions.length > 1 && (
                    <Button
                      className="delete-button-icon"
                      onClick={() => removeQuestion(index)}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" type="submit" disabled={loading}>
            {loading ? '保存中...' : (template ? '更新' : '作成')}
          </Button>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            キャンセル
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default QuestionTemplateForm;
